import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { FunctionalLoader } from 'seagull';
import { RequireVendorOnboardingComplete } from '~/components/RequireVendorOnboardingComplete/RequireVendorOnboardingComplete';
import { RouteWrapper } from '~/components/RouteWrapper';
import { routes } from '~/constants';
import { useStore, useStoreActions } from '~/contexts';
import { useEventReporter } from '~/event-reporter/EventReporter';
import { useTeamInvites } from '~/hooks/reactQuery/queries/useTeamInvites';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { clearPersistedOnboardingProgress } from '~/pages/VendorOnboarding/hooks/useOnboardingFlow';
import { selectors } from '~/store/state-selectors';
import { safeNextParam } from '~/utils/auth-redirect';
import { getHomePagePages } from '~/utils/routeConfigs';
import AcceptInvitationPage from '../AcceptInvitationPage/AcceptInvitationPage';
import { isUnknownHomeSubpath } from './MainPages.utils';
const SentryRoute = Sentry.withSentryRouting(Route);
function Logout() {
    const { reset } = useStoreActions();
    const { sendEventToGtm } = useEventReporter();
    useEffect(()=>{
        sendEventToGtm('logout', {});
        clearPersistedOnboardingProgress();
        reset(true);
    }, []);
    return /*#__PURE__*/ _jsx(Redirect, {
        to: routes.home
    });
}
export default function MainPages(props) {
    const { authorizedPages, unauthorizedPages, LoggedOutComponent } = props;
    const hasUserInfo = useStore(selectors.user.hasUserInfo);
    const isVendor = useStore(selectors.user.isVendor);
    const hasBusiness = useStore(selectors.user.hasBusiness);
    const location = useLocation();
    const featureFlags = useFeatureFlags();
    const homeRouteUrls = useMemo(()=>getHomePagePages(featureFlags).map((config)=>config.url), [
        featureFlags
    ]);
    const { teamInvites, isLoading: isLoadingTeamInvites } = useTeamInvites();
    const [invitationPageDismissed, setInvitationPageDismissed] = useState(false);
    const showInvitationPage = !invitationPageDismissed && teamInvites.length > 0 && !hasBusiness;
    const setShowInvitationPage = (show)=>setInvitationPageDismissed(!show);
    if (isLoadingTeamInvites) {
        return /*#__PURE__*/ _jsx(FunctionalLoader, {
            name: "team-invites-load",
            show: true
        });
    }
    const redirectToHome = ()=>{
        const nextPath = safeNextParam(location.search);
        if (nextPath != null) {
            return /*#__PURE__*/ _jsx(Redirect, {
                to: nextPath
            });
        }
        return showInvitationPage ? /*#__PURE__*/ _jsx(Redirect, {
            to: {
                pathname: routes.acceptInvitation,
                search: location.search
            }
        }) : /*#__PURE__*/ _jsx(Redirect, {
            to: {
                pathname: routes.home,
                search: location.search
            }
        });
    };
    return /*#__PURE__*/ _jsxs(Switch, {
        children: [
            unauthorizedPages.map((config)=>/*#__PURE__*/ _jsx(SentryRoute, {
                    path: config.url,
                    render: (props)=>/*#__PURE__*/ _jsx(RouteWrapper, _object_spread({
                            config: config
                        }, props))
                }, config.url)),
            showInvitationPage ? /*#__PURE__*/ _jsx(SentryRoute, {
                path: routes.acceptInvitation,
                render: ()=>/*#__PURE__*/ _jsx(AcceptInvitationPage, {
                        setShowInvitationPage: setShowInvitationPage
                    })
            }) : authorizedPages.filter((param)=>{
                let { vendorOnly } = param;
                return !vendorOnly || isVendor;
            }).map((config)=>/*#__PURE__*/ _jsx(SentryRoute, {
                    path: config.url,
                    render: (props)=>{
                        if (!hasUserInfo) {
                            if (config.url === routes.home && isUnknownHomeSubpath(location.pathname, homeRouteUrls)) {
                                return /*#__PURE__*/ _jsx(Redirect, {
                                    to: routes.home
                                });
                            }
                            return /*#__PURE__*/ _jsx(LoggedOutComponent, {});
                        } else {
                            return /*#__PURE__*/ _jsx(RequireVendorOnboardingComplete, {
                                children: /*#__PURE__*/ _jsx(RouteWrapper, _object_spread({
                                    config: config
                                }, props))
                            });
                        }
                    }
                }, config.url)),
            /*#__PURE__*/ _jsx(SentryRoute, {
                path: routes.login,
                render: redirectToHome
            }),
            /*#__PURE__*/ _jsx(SentryRoute, {
                path: routes.logout,
                component: Logout
            }),
            /*#__PURE__*/ _jsx(SentryRoute, {
                render: redirectToHome
            })
        ]
    });
}
